"use client";

import "./AssumptionModeToggle.css";

/** The two modes an assumption value can take. */
export type AssumptionMode = "point" | "range";

export interface AssumptionModeToggleProps {
  /** Current mode for the assumption value. */
  mode: AssumptionMode;
  /**
   * Called when the user clicks the toggle button.
   * Receives the mode to switch *to* (the opposite of the current mode).
   */
  onToggle: (next: AssumptionMode) => void;
}

/**
 * A compact icon button that lets the user switch an assumption between
 * single-point mode and band-of-uncertainty (range) mode.
 *
 * Renders a tooltip on hover / focus-visible describing the switch and its
 * effect on the value. Purely presentational — no internal state.
 */
export function AssumptionModeToggle({
  mode,
  onToggle,
}: AssumptionModeToggleProps) {
  const isPoint = mode === "point";
  const nextMode: AssumptionMode = isPoint ? "range" : "point";
  const ariaLabel = `Switch to ${nextMode === "point" ? "single value" : "range"}`;

  return (
    <span className="as-mode-toggle-wrap">
      <button
        type="button"
        className="as-mode-toggle"
        onClick={() => onToggle(nextMode)}
        aria-label={ariaLabel}
      >
        {isPoint ? (
          /* Point → expand to range icon */
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <title>Switch to range</title>
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="6" y1="9" x2="6" y2="15" />
            <line x1="18" y1="9" x2="18" y2="15" />
          </svg>
        ) : (
          /* Range → collapse to point icon */
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <title>Switch to single value</title>
            <line x1="3" y1="12" x2="21" y2="12" />
            <circle cx="12" cy="12" r="3" fill="currentColor" />
          </svg>
        )}
      </button>

      <span className="as-mode-tip" role="tooltip">
        <span className="as-mode-tip-kind">
          {isPoint ? "Switch to range" : "Switch to single value"}
        </span>
        <span className="as-mode-tip-desc">
          {isPoint
            ? "Replace the single value with a band of uncertainty plus a draggable nominal estimate inside it."
            : "Collapse the band to one fixed value. The current nominal becomes the value."}
        </span>
      </span>
    </span>
  );
}
